/**
 * Migration Runner - Backup → Apply → Validate → Commit | Rollback.
 *
 * EXECUTION PROTOCOL:
 *
 *   1. BACKUP   - Save raw data to a migration-backup key in localStorage.
 *                 This is the rollback point. Never skipped.
 *
 *   2. RECORD   - Write in-progress entry to MigrationHistory before any transform.
 *                 If the page crashes after backup but before commit, crash recovery
 *                 will find this record and restore from backup on next boot.
 *
 *   3. APPLY    - Execute each step in the migration chain sequentially.
 *                 Each step is deterministic. No randomness. No network calls.
 *
 *   4. VALIDATE - If the final migration definition has a validate() function,
 *                 run it on the result. Failure triggers rollback.
 *
 *   5a. COMMIT  - Save migrated data back to the original storage key.
 *                 Remove backup. Mark history as complete.
 *                 Emit migration:completed.
 *
 *   5b. ROLLBACK (on any error in steps 3-4)
 *                 Restore data from backup key.
 *                 Remove backup. Mark history as rolled-back.
 *                 Emit migration:rollback_applied + state:corrupted.
 *                 Return { success: false, data: null }.
 *
 * CALLER CONTRACT:
 *   The caller is responsible for:
 *     - Providing the raw pre-migration data
 *     - Writing the committed result back to its own storage layer
 *       (runner does NOT write to the final key - it returns the migrated data)
 *   Exception: the BACKUP key is written and removed by the runner.
 *
 * SYNCHRONOUS DESIGN:
 *   All current migrations operate exclusively on localStorage, which is synchronous.
 *   runMigrations() is therefore synchronous. If a future migration requires async
 *   operations (network, IndexedDB), introduce runMigrationsAsync() alongside this
 *   function rather than changing this signature - callers in synchronous read paths
 *   (persistLoad, SyncLedger.get, etc.) cannot be made async without cascading changes.
 */

import { logger }           from "@/system/logger";
import { eventBus }         from "@/runtime/eventBus";
import { migrationRegistry }  from "./migrationRegistry";
import { MigrationHistory }   from "./migrationHistory";
import type { MigrationResult, MigrationPathError } from "./types";

/* =========================================================
   BACKUP HELPERS
   ========================================================= */

const BACKUP_PREFIX = "nuvra-migration-backup-";

function backupKey(storageKey: string): string {
  return `${BACKUP_PREFIX}${storageKey}-${Date.now()}`;
}

function saveBackup(bKey: string, data: unknown): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(bKey, JSON.stringify(data));
  } catch (err) {
    logger.error("MigrationRunner: backup write failed", { bKey, err });
  }
}

function loadBackup(bKey: string): unknown | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(bKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function removeBackup(bKey: string): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(bKey);
}

/* =========================================================
   MIGRATION RUNNER
   ========================================================= */

export interface RunMigrationsOptions {
  /** Scope name in the registry (e.g. SCOPE_PAYLOAD, SCOPE_ENVELOPE). */
  scope:       string;
  /** Logical storage key for history + observability (e.g. "nuvra-project-{id}"). */
  storageKey:  string;
  /** Current version of the data. */
  fromVersion: number;
  /** Target version to migrate to. */
  toVersion:   number;
  /** The raw data to migrate. */
  data:        unknown;
}

/**
 * Run all migrations from fromVersion to toVersion.
 * Returns the migrated data on success, null on failure.
 *
 * Synchronous - all current migrations operate on localStorage only.
 * Emits observable migration events throughout execution.
 * Writes and removes backup from localStorage internally.
 */
export function runMigrations<T = unknown>(
  opts: RunMigrationsOptions,
): MigrationResult<T> {
  const { scope, storageKey, fromVersion, toVersion, data } = opts;
  const startMs = Date.now();

  // Nothing to do
  if (fromVersion === toVersion) {
    eventBus.emit({ type: "migration:skipped", storageKey, reason: "already at target version" });
    return {
      success:      true,
      data:         data as T,
      fromVersion,
      appliedTo:    toVersion,
      stepsApplied: 0,
      durationMs:   0,
    };
  }

  // Idempotency: already completed this migration
  if (MigrationHistory.hasCompleted(storageKey, fromVersion, toVersion)) {
    eventBus.emit({ type: "migration:skipped", storageKey, reason: "already completed" });
    return {
      success:      true,
      data:         data as T,
      fromVersion,
      appliedTo:    toVersion,
      stepsApplied: 0,
      durationMs:   0,
    };
  }

  // Resolve the migration chain - fail-fast if path is missing
  let chain;
  try {
    chain = migrationRegistry.getChain(scope, fromVersion, toVersion);
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    logger.error("MigrationRunner: no migration path", { scope, fromVersion, toVersion, reason });
    eventBus.emit({ type: "migration:failed", storageKey, fromVersion, toVersion, reason });
    return {
      success:      false,
      data:         null,
      fromVersion,
      appliedTo:    fromVersion,
      stepsApplied: 0,
      durationMs:   Date.now() - startMs,
      error:        reason,
    };
  }

  // ── STEP 1: BACKUP ──────────────────────────────────────
  const bKey = backupKey(storageKey);
  saveBackup(bKey, data);
  eventBus.emit({ type: "migration:backup_saved", storageKey, backupKey: bKey });

  // ── STEP 2: RECORD IN-PROGRESS ──────────────────────────
  MigrationHistory.markInProgress(storageKey, fromVersion, toVersion, bKey);
  eventBus.emit({ type: "migration:started", storageKey, fromVersion, toVersion });

  // ── STEP 3 + 4: APPLY CHAIN ─────────────────────────────
  let current: unknown = data;
  let stepIndex = 0;

  try {
    for (const def of chain) {
      eventBus.emit({
        type:        "migration:step",
        storageKey,
        stepIndex,
        stepCount:   chain.length,
        fromVersion: def.fromVersion,
        toVersion:   def.toVersion,
      });

      current = def.migrate(current);

      // Optional post-step validation
      if (def.validate && !def.validate(current)) {
        throw new Error(
          `Post-migration validation failed at step v${def.fromVersion}→v${def.toVersion}`,
        );
      }

      stepIndex++;
      logger.debug("MigrationRunner: step applied", {
        storageKey,
        from: def.fromVersion,
        to:   def.toVersion,
      });
    }

    // ── STEP 5a: COMMIT ─────────────────────────────────────
    const durationMs = Date.now() - startMs;
    removeBackup(bKey);
    MigrationHistory.markComplete(storageKey, fromVersion, toVersion);
    eventBus.emit({ type: "migration:completed", storageKey, fromVersion, toVersion, durationMs });
    logger.info("MigrationRunner: migration complete", {
      storageKey,
      fromVersion,
      toVersion,
      stepsApplied: stepIndex,
      durationMs,
    });

    return {
      success:      true,
      data:         current as T,
      fromVersion,
      appliedTo:    toVersion,
      stepsApplied: stepIndex,
      durationMs,
    };

  } catch (err) {
    // ── STEP 5b: ROLLBACK ───────────────────────────────────
    const reason = err instanceof Error ? err.message : String(err);
    logger.error("MigrationRunner: migration failed - rolling back", {
      storageKey,
      fromVersion,
      toVersion,
      stepIndex,
      reason,
    });

    // Attempt to restore from backup
    const restored = loadBackup(bKey);
    removeBackup(bKey);

    MigrationHistory.markRolledBack(storageKey, fromVersion, toVersion, reason);

    if (restored !== null) {
      eventBus.emit({ type: "migration:rollback_applied", storageKey, backupKey: bKey });
    }

    eventBus.emit({ type: "migration:failed", storageKey, fromVersion, toVersion, reason });
    eventBus.emit({ type: "state:corrupted", projectId: storageKey, reason: `Migration failed: ${reason}` });

    return {
      success:      false,
      data:         null,
      fromVersion,
      appliedTo:    fromVersion,
      stepsApplied: stepIndex,
      durationMs:   Date.now() - startMs,
      error:        reason,
      rolledBack:   restored !== null,
    };
  }
}

/* =========================================================
   CRASH RECOVERY RUNNER
   ========================================================= */

/**
 * Runs at app boot via initMigrationEngine().
 * Scans MigrationHistory for in-progress records from a previous crashed session.
 *
 * For each active crash record:
 *   - If backup exists: restore it (migration never committed) → mark rolled-back
 *   - If backup absent: data may already be at toVersion (crash after commit);
 *     the next load will detect version and skip migration (idempotency)
 *
 * Stale crash records (> 24h) are marked failed without recovery attempt.
 */
export function recoverCrashedMigrations(): void {
  if (typeof localStorage === "undefined") return;

  const { active, stale } = MigrationHistory.getPendingCrashRecovery();

  // Handle stale crash records - too old to safely recover, mark failed
  for (const record of stale) {
    logger.warn("MigrationRunner: stale crash record - marking failed", {
      storageKey:  record.storageKey,
      fromVersion: record.fromVersion,
      toVersion:   record.toVersion,
      startedAt:   record.startedAt,
    });
    MigrationHistory.markFailed(
      record.storageKey,
      record.fromVersion,
      record.toVersion,
      "Stale crash record - exceeded 24h recovery window",
    );
  }

  // Handle active crash records
  for (const record of active) {
    const bKey = record.backupKey;

    if (bKey && localStorage.getItem(bKey) !== null) {
      // Backup exists → migration never committed → restore backup
      const backup = loadBackup(bKey);
      removeBackup(bKey);

      if (backup !== null) {
        // Write backup back to original key
        try {
          localStorage.setItem(record.storageKey, JSON.stringify(backup));
        } catch (err) {
          logger.error("MigrationRunner: crash recovery restore failed", {
            storageKey: record.storageKey,
            err,
          });
        }
      }

      MigrationHistory.markRolledBack(
        record.storageKey,
        record.fromVersion,
        record.toVersion,
        "Crash recovery: backup restored on boot",
      );

      eventBus.emit({ type: "migration:rollback_applied", storageKey: record.storageKey, backupKey: bKey });
      logger.info("MigrationRunner: crash recovery - backup restored", {
        storageKey: record.storageKey,
      });

    } else {
      // No backup - crash may have happened after commit
      // The data is at whatever version it was left at.
      // On next load, version detection will handle the appropriate action.
      MigrationHistory.markFailed(
        record.storageKey,
        record.fromVersion,
        record.toVersion,
        "Crash recovery: backup not found - data left at current version",
      );
      logger.warn("MigrationRunner: crash recovery - no backup found, leaving data as-is", {
        storageKey: record.storageKey,
      });
    }
  }

  if (active.length > 0 || stale.length > 0) {
    logger.info("MigrationRunner: crash recovery complete", {
      recovered: active.length,
      stale:     stale.length,
    });
  }
}
